import { View, Flex, Link, ListBox, Item } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import FilterComponent from '../component/FilterComponent';
import FilterSearchTextBox from '../component/FilterSearchTextBox';
import CardComponentMobile from '../component/CardComponentMobile';
import { Loader } from './Loader';
import type { ProjectItem, ProjectStatus, QueryParams } from '../types';
import type { Selection } from '@react-types/shared';
import type { CustomView } from '../../../../services/soap/dataObjects/CustomView';
import type { SettingItem } from '../../../../services/soap/features/responses/GetFormSettingsResponse';
import styles from '../project_activities.module.css';

interface DeviceInfo {
  shouldUseMobileLayout: () => boolean;
  screenHeight: number;
  isTablet?: boolean;
  isIPad?: boolean;
}

export interface MobileListViewProps {
  showEmptyViews?: boolean;
  filterParams: QueryParams;
  onFilterChange: (params: QueryParams) => void;
  onSearchChange: (params: QueryParams) => void;
  searchedText: string;
  fpaTypeCategory: string;
  items: ProjectItem[];
  rowsData: ProjectItem[];
  projectStatusList: ProjectStatus[];
  selectedKeys: Set<string | number> | undefined;
  onRowClick: (selection: Selection) => void;
  onAddNew: () => void;
  isLoading: boolean;
  loadMore: () => void;
  deviceInfo: DeviceInfo;
  listBoxRef: React.RefObject<HTMLDivElement | null>;
  customViews?: CustomView[];
  hasMoreCustomViews?: boolean;
  onLoadMoreCustomViews?: () => void;
  onRefreshCustomViews?: () => void;
  formSettings?: SettingItem[];
  initialViewId?: string;
  onCreateView?: () => void;
  onEditView?: (view: CustomView) => void;
  onDuplicateView?: (view: CustomView) => void;
  onDeleteViewClick?: (view: CustomView) => void;
  resetBaselineRef?: React.MutableRefObject<(() => void) | null>;
}

export function MobileListView({
  showEmptyViews,
  filterParams,
  onFilterChange,
  onSearchChange,
  searchedText,
  fpaTypeCategory,
  items,
  rowsData,
  projectStatusList,
  selectedKeys,
  onRowClick,
  onAddNew,
  isLoading,
  loadMore,
  deviceInfo,
  listBoxRef,
  customViews,
  hasMoreCustomViews,
  onLoadMoreCustomViews,
  onRefreshCustomViews,
  formSettings,
  initialViewId,
  onCreateView,
  onEditView,
  onDuplicateView,
  onDeleteViewClick,
  resetBaselineRef,
}: MobileListViewProps) {
  const { t } = useTranslation();

  return (
    <View
      width="100%"
      height="100%"
      UNSAFE_style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <View UNSAFE_style={{ height: '60px', flexShrink: 0, backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <FilterComponent
          showEmptyViews={showEmptyViews}
          onFilterChange={onFilterChange}
          filterParams={filterParams}
          customViews={customViews}
          hasMoreCustomViews={hasMoreCustomViews}
          onLoadMoreCustomViews={onLoadMoreCustomViews}
          onRefreshCustomViews={onRefreshCustomViews}
          formSettings={formSettings}
          fpaTypeCategory={fpaTypeCategory}
          initialViewId={initialViewId}
          onCreateView={onCreateView}
          onEditView={onEditView}
          onDuplicateView={onDuplicateView}
          onDeleteViewClick={onDeleteViewClick}
          resetBaselineRef={resetBaselineRef}
          deviceInfo={deviceInfo}
        />
      </View>

      {!showEmptyViews && (
        <View UNSAFE_style={{ height: '50px', flexShrink: 0, backgroundColor: 'white', padding: '5px 10px', borderBottom: '1px solid #e0e0e0' }}>
        <Flex direction="row" height="40px" alignItems="center" justifyContent="end" gap="size-100">
          <FilterSearchTextBox
            onSearchChange={onSearchChange}
            filterParams={filterParams}
            searchedText={searchedText}
            fpaTypeCategory={fpaTypeCategory}
          />
          <i className={`${styles.icon_search} bi bi-search`} />
        </Flex>
      </View>
      )}

      <View flex={1} overflow="auto" UNSAFE_style={{ padding: '5px', backgroundColor: '#f5f6f9' }}>
        {isLoading ? (
          <View UNSAFE_style={{ padding: '20px' }}>
            <Loader />
          </View>
        ) : (
          <ListBox
            ref={listBoxRef as never}
            id="listProjectCardsMobile"
            selectionMode="single"
            onSelectionChange={onRowClick}
            minHeight="130px"
            maxHeight={deviceInfo.shouldUseMobileLayout() ? `${deviceInfo.screenHeight}px` : '100%'}
            selectedKeys={selectedKeys ?? new Set()}
            aria-label="Project Activities Cards Mobile"
            items={items?.length > 0 ? items : rowsData || []}
            isLoading={isLoading}
            onLoadMore={loadMore}
            UNSAFE_style={{ width: '100%', backgroundColor: 'transparent' }}
          >
            {(item: ProjectItem) => (
              <Item key={item.id} textValue={item.name}>
                <CardComponentMobile item={item} projectStatusList={projectStatusList} />
              </Item>
            )}
          </ListBox>
        )}
      </View>

      <View position="absolute" right="16px" bottom="95px" zIndex={10}>
        <Flex height="48px" justifyContent="center" alignItems="center" gap="size-100" UNSAFE_className={styles.add_new_parent}>
          <Link isQuiet onPress={onAddNew} UNSAFE_className={styles.add_new_btn}>
            <i className="bi bi-plus fs-5">
              <View UNSAFE_className={styles.add_new_btn_text}>{t('add_new', { ns: 'layout_components' })}</View>
            </i>
          </Link>
        </Flex>
      </View>
    </View>
  );
}
